import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import {
  Check, X, Storefront, ArrowSquareOut, Star, Trash, MagnifyingGlass,
} from "@phosphor-icons/react";
import { toast } from "sonner";

export default function ReviewsPanel({ pending, onModerated }) {
  const [tab, setTab] = useState("pending"); // "pending" | "all"
  const [search, setSearch] = useState("");
  const [allReviews, setAllReviews] = useState([]);
  const [loadingAll, setLoadingAll] = useState(false);

  const moderate = async (id, approve) => {
    await api.post(`/admin/reviews/${id}/moderate?approve=${approve}`);
    onModerated?.(id);
  };

  const deleteReview = async (id, authorName) => {
    if (!window.confirm(
      `Supprimer DÉFINITIVEMENT l'avis de "${authorName}" ?\n\n` +
      "Cette action est irréversible et recalculera la note moyenne de la fiche."
    )) return;
    try {
      const r = await api.delete(`/admin/reviews/${id}`);
      if (r.data?.ok) {
        toast.success("Avis supprimé");
        // Refresh both lists
        onModerated?.(id);
        setAllReviews((cur) => cur.filter((x) => x.id !== id));
      } else {
        toast.error("Avis introuvable");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Suppression impossible");
    }
  };

  const searchAll = async () => {
    setLoadingAll(true);
    try {
      const r = await api.get(`/admin/reviews/all?search=${encodeURIComponent(search)}&limit=200`);
      setAllReviews(r.data || []);
    } catch (e) {
      toast.error("Recherche impossible");
    } finally {
      setLoadingAll(false);
    }
  };

  const displayList = tab === "pending" ? pending : allReviews;

  return (
    <div className="space-y-4" data-testid="admin-reviews">
      {/* Tabs En attente / Tous */}
      <div className="flex items-center gap-1 border-b border-border">
        <button
          type="button"
          onClick={() => setTab("pending")}
          className={`px-3 py-2 text-xs uppercase tracking-widest font-bold border-b-2 transition-colors ${
            tab === "pending"
              ? "border-primary text-primary"
              : "border-transparent text-muted-foreground hover:text-foreground"
          }`}
          data-testid="reviews-tab-pending"
        >
          En attente ({pending.length})
        </button>
        <button
          type="button"
          onClick={() => setTab("all")}
          className={`px-3 py-2 text-xs uppercase tracking-widest font-bold border-b-2 transition-colors ${
            tab === "all"
              ? "border-primary text-primary"
              : "border-transparent text-muted-foreground hover:text-foreground"
          }`}
          data-testid="reviews-tab-all"
        >
          Tous les avis
        </button>
      </div>

      {/* Barre de recherche uniquement en mode "Tous" */}
      {tab === "all" && (
        <div className="flex items-center gap-2" data-testid="reviews-search-bar">
          <div className="flex-1 relative">
            <MagnifyingGlass size={12} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"/>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && searchAll()}
              placeholder="Nom d'auteur, mot dans le commentaire, ex : « TEST »"
              className="w-full text-sm border border-border rounded-sm pl-9 pr-3 py-2 bg-background focus:border-primary outline-none"
              data-testid="reviews-search-input"
            />
          </div>
          <button
            type="button"
            onClick={searchAll}
            disabled={loadingAll}
            className="bg-primary text-primary-foreground hover:bg-[#0f3d29] hover:text-white px-4 py-2 rounded-sm text-xs font-bold uppercase tracking-widest disabled:opacity-50"
            data-testid="reviews-search-submit"
          >
            {loadingAll ? "…" : "Rechercher"}
          </button>
        </div>
      )}

      {displayList.length === 0 ? (
        <div className="bg-card border border-dashed border-border rounded-sm p-12 text-center text-muted-foreground">
          {tab === "pending"
            ? "Aucun avis en attente."
            : search
            ? "Aucun résultat pour cette recherche."
            : "Tape un mot-clé et clique sur Rechercher pour trouver des avis à supprimer."}
        </div>
      ) : (
        displayList.map((r) => (
          <div key={r.id} className="bg-card border border-border rounded-sm p-4 flex flex-col sm:flex-row items-start justify-between gap-4" data-testid={`review-row-${r.id}`}>
            <div className="flex-1 min-w-0">
              {r.business ? (
                <div className="flex items-center gap-1.5 flex-wrap mb-2" data-testid={`review-business-${r.id}`}>
                  <Storefront size={14} weight="duotone" className="text-primary shrink-0"/>
                  <span className="font-display font-semibold text-sm">{r.business.name}</span>
                  {r.business.city && <span className="text-xs text-muted-foreground">· {r.business.city}</span>}
                  {r.business.tier && r.business.tier !== "free" && (
                    <span className="text-[9px] uppercase tracking-widest font-bold bg-primary/10 text-primary rounded-sm px-1.5 py-0.5">
                      {r.business.tier === "premium_ai" ? "Premium IA" : "Premium"}
                    </span>
                  )}
                  {r.business.slug && (
                    <Link
                      to={`/entreprise/${r.business.slug}`}
                      target="_blank"
                      rel="noreferrer"
                      className="text-primary hover:underline text-xs inline-flex items-center gap-0.5 shrink-0"
                      data-testid={`review-view-fiche-${r.id}`}
                    >
                      Voir la fiche <ArrowSquareOut size={10} weight="bold"/>
                    </Link>
                  )}
                </div>
              ) : (
                <div className="text-[11px] text-destructive mb-2" data-testid={`review-orphan-${r.id}`}>
                  ⚠️ Fiche entreprise supprimée (id: {r.business_id})
                </div>
              )}

              <div className="flex items-center gap-2 mb-1">
                <span className="font-semibold text-sm">{r.author_name}</span>
                <span className="flex items-center gap-0.5 text-[#E0B043]">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <Star key={n} size={12} weight={n <= r.rating ? "fill" : "regular"} />
                  ))}
                </span>
                <span className="text-xs text-muted-foreground">{r.rating}/5</span>
                {r.moderated === true && r.approved === true && (
                  <span className="text-[9px] uppercase tracking-widest font-bold bg-primary/10 text-primary rounded-sm px-1.5 py-0.5">Publié</span>
                )}
                {r.moderated === true && r.approved === false && (
                  <span className="text-[9px] uppercase tracking-widest font-bold bg-muted text-muted-foreground rounded-sm px-1.5 py-0.5">Rejeté</span>
                )}
                {r.moderated === false && (
                  <span className="text-[9px] uppercase tracking-widest font-bold bg-secondary/15 text-secondary rounded-sm px-1.5 py-0.5">En attente</span>
                )}
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">{r.text || r.comment}</p>
              {r.created_at && (
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground/70 mt-1 font-mono">
                  {String(r.created_at).slice(0, 19).replace("T", " ")}
                </p>
              )}
            </div>

            <div className="flex gap-1 shrink-0 self-end sm:self-start flex-wrap justify-end">
              {tab === "pending" && (
                <>
                  <button
                    onClick={() => moderate(r.id, true)}
                    className="p-2 bg-primary text-primary-foreground hover:bg-[#0f3d29] hover:text-white rounded-sm inline-flex items-center gap-1"
                    title="Approuver et publier"
                    data-testid={`approve-${r.id}`}
                  >
                    <Check size={14}/> <span className="text-[11px] font-semibold">Approuver</span>
                  </button>
                  <button
                    onClick={() => moderate(r.id, false)}
                    className="p-2 bg-card border border-border text-muted-foreground hover:border-foreground hover:text-foreground rounded-sm inline-flex items-center gap-1"
                    title="Rejeter (masque mais garde en base)"
                    data-testid={`reject-${r.id}`}
                  >
                    <X size={14}/> <span className="text-[11px] font-semibold">Rejeter</span>
                  </button>
                </>
              )}
              <button
                onClick={() => deleteReview(r.id, r.author_name)}
                className="p-2 bg-card border border-destructive text-destructive hover:bg-destructive hover:text-destructive-foreground rounded-sm inline-flex items-center gap-1"
                title="Supprimer définitivement"
                data-testid={`delete-review-${r.id}`}
              >
                <Trash size={14} weight="bold"/> <span className="text-[11px] font-semibold">Supprimer</span>
              </button>
            </div>
          </div>
        ))
      )}
    </div>
  );
}
